/**
 * KnowledgeOverviewPanel — vibe-designer 1.169.0 Phase 2.
 *
 * Right-side content for the "Overview" nav item. Renders a 6-up grid
 * of KnowledgeStatCards driven by knowledgeStore.overview.
 */
import { PlusCircle, Search, Gauge } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { useTenantStore } from '@/stores/tenantStore';
import { KnowledgeStatCard } from './KnowledgeStatCard';
import { KnowledgeAskBox } from './KnowledgeAskBox';
import { KnowledgeAllSearchBar } from './KnowledgeAllSearchBar';
import { KnowledgeIngestPanel } from './KnowledgeIngestPanel';
import { KnowledgeRecentRuns } from './KnowledgeRecentRuns';
import { MindPillarInventory } from './MindPillarInventory';
import { KnowledgeZone } from './KnowledgeZone';
import type { KnowledgeServiceStats } from '@/services/knowledgeApi';

const SERVICE_LABELS: Record<KnowledgeServiceStats['service'], string> = {
  mind: 'THE MIND',
  cortex: 'THE CORTEX',
  brain: 'THE BRAIN',
  sight: 'THE SIGHT',
  cognition: 'THE COGNITION',
  obsidian: 'OBSIDIAN',
};

export function KnowledgeOverviewPanel() {
  const overview = useKnowledgeStore((s) => s.overview);
  const isLoading = useKnowledgeStore((s) => s.isLoading);
  const tenantId = useTenantStore((s) => s.tenantId);
  // 1.185.0 Phase 7.2 — web_search availability for the 7th stat card.
  const mcpCapabilities = useTenantStore((s) => s.mcpCapabilities);

  if (overview === null) {
    return (
      <div
        data-testid="knowledge-overview-loading"
        className="flex-1 flex items-center justify-center text-vibe-text-secondary text-sm p-8"
      >
        {isLoading ? 'Loading overview…' : 'Overview not loaded yet.'}
      </div>
    );
  }

  // 2026-06-29 redesign — header corpus rollup. Sums known counts and reports
  // how many systems are online (6 corpora + Web Search = 7) so the user sees
  // the bottom-line state without scrolling to the stat grid.
  const totalItems = overview.reduce((acc, s) => acc + (s.count ?? 0), 0);
  const corporaOnline = overview.filter((s) => s.available).length;
  const webOnline = mcpCapabilities?.web_search ? 1 : 0;
  const servicesOnline = corporaOnline + webOnline;
  const servicesTotal = overview.length + 1;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <header className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-vibe-text">Knowledge</h1>
          <p className="text-sm text-vibe-text-secondary">
            Tenant: <span className="font-mono text-vibe-text">{tenantId ?? '—'}</span>
          </p>
        </div>
        <div
          data-testid="knowledge-corpus-rollup"
          className="flex items-center gap-4 rounded-lg border border-vibe-border-strong bg-vibe-surface px-4 py-2"
        >
          <div className="text-right">
            <div className="text-lg font-semibold tabular-nums text-vibe-text leading-none">
              {totalItems.toLocaleString()}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-vibe-text-secondary mt-1">
              corpus items
            </div>
          </div>
          <div className="h-8 w-px bg-vibe-border" />
          <div className="text-right">
            <div className="text-lg font-semibold tabular-nums text-vibe-text leading-none">
              <span className={servicesOnline === servicesTotal ? 'text-vibe-success' : 'text-vibe-warning'}>
                {servicesOnline}
              </span>
              <span className="text-vibe-text-secondary">/{servicesTotal}</span>
            </div>
            <div className="text-[11px] uppercase tracking-wide text-vibe-text-secondary mt-1">
              online
            </div>
          </div>
        </div>
      </header>

      {/* Zone ① — ACTION: add content to the corpus. */}
      <KnowledgeZone
        testid="knowledge-zone-add"
        icon={PlusCircle}
        title="Add knowledge"
        description="Ingest text, files, folders, URLs, repos or images into your knowledge systems."
        accentTextClass="text-vibe-accent"
        accentBgClass="bg-vibe-accent/10"
      >
        <KnowledgeIngestPanel />
      </KnowledgeZone>

      {/* Zone ② — QUERY: ask the corpus or search across systems. */}
      <KnowledgeZone
        testid="knowledge-zone-query"
        icon={Search}
        title="Ask & search"
        description="Get a grounded answer from the corpus, or search raw passages across every system."
        accentTextClass="text-vibe-svc-websearch"
        accentBgClass="bg-vibe-svc-websearch/10"
      >
        <KnowledgeAskBox />
        <KnowledgeAllSearchBar />
      </KnowledgeZone>

      {/* Zone ③ — OBSERVE: recent activity + corpus stats. */}
      <KnowledgeZone
        testid="knowledge-zone-status"
        icon={Gauge}
        title="Corpus status"
        description="Recent ingest activity, pillar distribution, and per-system counts."
        accentTextClass="text-vibe-text-secondary"
        accentBgClass="bg-vibe-surface-raised"
      >
        <KnowledgeRecentRuns />
        {/* 1.190.0 Phase 7.6 — MIND pillar inventory near the MIND stat card. */}
        <MindPillarInventory />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {overview.map((svc) => (
            <KnowledgeStatCard
              key={svc.service}
              label={SERVICE_LABELS[svc.service]}
              value={svc.count}
              sublabel={svc.label ?? undefined}
              available={svc.available}
              service={svc.service}
              error={svc.error}
            />
          ))}
          {/* 1.185.0 Phase 7.2 — 7th stat card for Web Search. NOT in
              overview[] (no count); sourced from mcpCapabilities. */}
          <KnowledgeStatCard
            label="Web Search"
            value={null}
            sublabel="query-time only"
            available={mcpCapabilities?.web_search ?? false}
            service="web_search"
            error={null}
          />
        </div>
      </KnowledgeZone>
    </div>
  );
}
